import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/LoginPage";
import LandingPage from "@/pages/LandingPage";
import PublicVisitRequest from "@/pages/PublicVisitRequest";
import Visitors from "@/pages/Visitors";
import ScrambleBoard from "@/pages/ScrambleBoard";
import Dashboard from "@/pages/Dashboard";
import RequestForm from "@/pages/RequestForm";
import RequestDetail from "@/pages/RequestDetail";
import StakeholderManager from "@/pages/StakeholderManager";
import ProfilePage from "@/pages/ProfilePage";
import UserManagement from "@/pages/UserManagement";
import AuditLog from "@/pages/AuditLog";
import VisitorCalendar from "@/pages/VisitorCalendar";
import EmailTemplates from "@/pages/EmailTemplates";
import Organizations from "@/pages/Organizations";
import HousingUnits from "@/pages/HousingUnits";
import Cleaners from "@/pages/Cleaners";
import Bookings from "@/pages/Bookings";
import HousingCalendar from "@/pages/HousingCalendar";
import HousingAnalytics from "@/pages/HousingAnalytics";
import RateCards from "@/pages/RateCards";
import Invoices from "@/pages/Invoices";
import Worksheet from "@/pages/Worksheet";
import Leases from "@/pages/Leases";
import HelpersCalendar from "@/pages/HelpersCalendar";
import HelperTasks from "@/pages/HelperTasks";
import MedicalLicenses from "@/pages/MedicalLicenses";
import Expenses from "@/pages/Expenses";
import Staff from "@/pages/Staff";
import Finance from "@/pages/Finance";
import Forecast from "@/pages/Forecast";
import OrgSettings from "@/pages/OrgSettings";
import Contacts from "@/pages/Contacts";
import RolesPage from "@/pages/RolesPage";
import HelperTaskRules from "@/pages/HelperTaskRules";
import HelperTasksPrint from "@/pages/HelperTasksPrint";
import SetPassword from "@/pages/SetPassword";
import MyLease from "@/pages/MyLease";
import MyInvoices from "@/pages/MyInvoices";
import MyPayments from "@/pages/MyPayments";
import DepartmentsPage from "@/pages/DepartmentsPage";
import TransportList from "@/pages/TransportList";
import GuestRequests from "@/pages/GuestRequests";
import GuestRequestForm from "@/pages/GuestRequestForm";
import GuestRequestDetail from "@/pages/GuestRequestDetail";
import SponsorOrgs from "@/pages/SponsorOrgs";
import ApprovalRouting from "@/pages/ApprovalRouting";
import Surveys from "@/pages/Surveys";
import { Sidebar } from "@/components/Sidebar";
import { NotificationPanel } from "@/components/NotificationPanel";
import { Toaster } from "@/components/ui/sonner";
import { Menu } from "lucide-react";
import { useState } from "react";
import "@/App.css";

function ProtectedRoute() {
  const { user, organization, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#FCFCFB]" data-testid="loading-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#1B4332] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#525C58]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="flex h-screen bg-[#FCFCFB]">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      <div className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-200 lg:static lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <header
          className="border-b border-[#E8E8E6] bg-white flex items-center justify-between px-4 md:px-6 shrink-0"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            minHeight: 'calc(4rem + env(safe-area-inset-top))',
          }}
          data-testid="top-bar"
        >
          {/* Brand on the left (replaces the hamburger that used to clash with the iOS clock) */}
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-semibold text-[#1B4332] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {organization?.display_name || organization?.name || 'Mission Hospitality'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationPanel />
            <div className="text-sm text-[#525C58] font-medium hidden sm:block" data-testid="user-display-name">{user.name}</div>
            {/* Hamburger moved to the right — far from the iOS status-bar clock and within thumb reach */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-[#F4F4F1] rounded-lg transition-colors -mr-1"
              data-testid="mobile-menu-btn"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6 text-[#1B4332]" />
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto px-4 py-6 md:px-8 lg:px-12 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/visit" element={<PublicVisitRequest />} />
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/my/lease" element={<MyLease />} />
            <Route path="/my/invoices" element={<MyInvoices />} />
            <Route path="/my/payments" element={<MyPayments />} />
            <Route path="/requests/new" element={<RequestForm />} />
            <Route path="/requests/:id/edit" element={<RequestForm />} />
            <Route path="/requests/:id" element={<RequestDetail />} />
            <Route path="/stakeholders" element={<StakeholderManager />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/users" element={<UserManagement />} />
            <Route path="/departments" element={<DepartmentsPage />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/email-templates" element={<EmailTemplates />} />
            <Route path="/approval-routing" element={<ApprovalRouting />} />
            <Route path="/surveys" element={<Surveys />} />
            <Route path="/organizations" element={<Organizations />} />
            <Route path="/calendar" element={<VisitorCalendar />} />
            <Route path="/housing-units" element={<HousingUnits />} />
            <Route path="/cleaners" element={<Cleaners />} />
            <Route path="/bookings" element={<Bookings />} />
            <Route path="/housing-calendar" element={<HousingCalendar />} />
            <Route path="/scramble" element={<ScrambleBoard />} />
            <Route path="/visitors" element={<Visitors />} />
            <Route path="/housing-analytics" element={<HousingAnalytics />} />
            <Route path="/rate-cards" element={<RateCards />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/worksheet" element={<Worksheet />} />
            <Route path="/leases" element={<Leases />} />
            <Route path="/helpers-calendar" element={<HelpersCalendar />} />
            <Route path="/helper-tasks" element={<HelperTasks />} />
            <Route path="/transport" element={<TransportList />} />
            <Route path="/guest-requests" element={<GuestRequests />} />
            <Route path="/guest-requests/new" element={<GuestRequestForm />} />
            <Route path="/guest-requests/:id" element={<GuestRequestDetail />} />
            <Route path="/sponsor-orgs" element={<SponsorOrgs />} />
            <Route path="/medical-licenses" element={<MedicalLicenses />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/staff" element={<Staff />} />
            <Route path="/finance" element={<Finance />} />
            <Route path="/forecast" element={<Forecast />} />
            <Route path="/org-settings" element={<OrgSettings />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/roles" element={<RolesPage />} />
            <Route path="/helper-task-rules" element={<HelperTaskRules />} />
            <Route path="/helper-tasks/print" element={<HelperTasksPrint />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
